/*
 * ah-product-widget.js — ABANA
 * -----------------------------------------------------------------------------
 * Reusable, JS-based product-grid block for static blog-article / page bodies.
 *
 * The content team drops a marker div into an article/page body (authored via the
 * Admin API as plain HTML, NOT Liquid), e.g.:
 *
 *   <div class="ah-product-widget" data-source="collection:bonsai-plants" data-count="9"></div>
 *
 * On render, this script scans for those markers, fetches products client-side
 * from the public /collections/{handle}/products.json endpoint (no auth), and
 * renders a card grid consistent with the theme's collection cards, plus an
 * optional "reader discount" banner.
 *
 * DISCOUNT CODE: intentionally NOT stored per-marker. It lives in the single
 * global below so it can be created in Shopify Admin and swapped in ONE place —
 * without editing every article body. Empty string = banner hidden (safe default).
 * -----------------------------------------------------------------------------
 */
(function () {
  'use strict';

  // ── Single source of truth for the reader-discount offer ───────────────────
  // Set AH_READER_DISCOUNT.code once the code exists in Shopify Admin. While it
  // is empty, the widget renders the product grid only (no banner).
  var AH_READER_DISCOUNT = window.AH_READER_DISCOUNT || {
    code: '',                                   // e.g. 'READER20'  ← set after creating in Admin
    text: 'Get 20% off — a thank-you for our readers'
  };

  var FALLBACK_HANDLE = 'bonsai-plants';        // used when a source is empty / missing
  var DEFAULT_COUNT = 8;
  var MAX_COUNT = 24;
  var cache = {};                               // handle -> Promise (dedupe fetches on a page)

  function fmtINR(price) {
    // products.json prices are major-unit strings, e.g. "1667.00"
    var n = Math.round(parseFloat(price));
    if (isNaN(n)) return '';
    return '₹' + n.toLocaleString('en-IN');
  }

  function fetchCollection(handle, count) {
    if (!cache[handle]) {
      var limit = Math.min(Math.max(count, 1), 250);
      cache[handle] = fetch('/collections/' + encodeURIComponent(handle) + '/products.json?limit=' + limit, {
        headers: { 'Accept': 'application/json' }
      })
        .then(function (r) { return r.ok ? r.json() : { products: [] }; })
        .then(function (d) { return (d && d.products) || []; })
        .catch(function () { return []; });
    }
    return cache[handle];
  }

  // data-source forms: "collection:handle" (primary) or "tag:handle".
  // Both resolve against a collection of that handle; tag support is best-effort
  // (Shopify commonly auto-creates a same-handle collection) with the same fallback.
  function handleFromSource(source) {
    if (!source) return FALLBACK_HANDLE;
    var parts = source.split(':');
    var handle = (parts.length > 1 ? parts.slice(1).join(':') : parts[0]).trim();
    return handle || FALLBACK_HANDLE;
  }

  function cardHTML(product) {
    var variant = (product.variants || [])[0] || {};
    var img = (product.images || [])[0] || {};
    var url = '/products/' + product.handle;
    var imgSrc = img.src ? img.src : '';
    var priceHTML = variant.price ? '<span class="ah-pw-card__price">' + fmtINR(variant.price) + '</span>' : '';
    var imgHTML = imgSrc
      ? '<img class="ah-pw-card__img" src="' + imgSrc + '&width=400" alt="' +
        String(product.title).replace(/"/g, '&quot;') + '" loading="lazy" width="400" height="400">'
      : '<span class="ah-pw-card__img ah-pw-card__img--empty" aria-hidden="true"></span>';
    return (
      '<a class="ah-pw-card" href="' + url + '">' +
        '<span class="ah-pw-card__media">' + imgHTML + '</span>' +
        '<span class="ah-pw-card__title">' + product.title + '</span>' +
        priceHTML +
      '</a>'
    );
  }

  function bannerHTML() {
    if (!AH_READER_DISCOUNT.code) return '';
    return (
      '<div class="ah-pw-banner">' +
        '<span class="ah-pw-banner__text">' + AH_READER_DISCOUNT.text + '</span>' +
        '<span class="ah-pw-banner__code">Use code <strong>' + AH_READER_DISCOUNT.code + '</strong> at checkout</span>' +
      '</div>'
    );
  }

  function render(el, handle, products) {
    if (!products.length) { el.style.display = 'none'; return; }
    var count = parseInt(el.getAttribute('data-count'), 10) || DEFAULT_COUNT;
    count = Math.min(count, MAX_COUNT);
    var shown = products.slice(0, count);
    var grid = shown.map(cardHTML).join('');
    var viewAll = '<a class="ah-pw-viewall" href="/collections/' + handle + '">View all products →</a>';
    el.innerHTML = bannerHTML() + '<div class="ah-pw-grid">' + grid + '</div>' + viewAll;
    el.setAttribute('data-ah-pw-rendered', '1');
  }

  function hydrate(el) {
    if (el.getAttribute('data-ah-pw-rendered')) return;
    var count = parseInt(el.getAttribute('data-count'), 10) || DEFAULT_COUNT;
    var handle = handleFromSource(el.getAttribute('data-source'));
    fetchCollection(handle, count).then(function (products) {
      if (products.length) return render(el, handle, products);
      if (handle === FALLBACK_HANDLE) { el.style.display = 'none'; return; }
      // Source empty/missing → fall back to the main bonsai collection.
      return fetchCollection(FALLBACK_HANDLE, count).then(function (fb) {
        render(el, FALLBACK_HANDLE, fb);
      });
    });
  }

  function init() {
    var nodes = document.querySelectorAll('.ah-product-widget');
    for (var i = 0; i < nodes.length; i++) hydrate(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
